import { useAtom, useAtomValue, useSetAtom } from "jotai"
import * as Tabs from "@radix-ui/react-tabs"
import * as ToggleGroup from "@radix-ui/react-toggle-group"
import clsx from "clsx"
import { cn } from "@/utils/classname"
import { Switch } from "../ui/Switch"
import { ScrollBar } from "../ui/ScrollBar"
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/Select"
import {
	autoStraightenAtom,
	autoPerspectiveAtom,
	forwardProcessingAtom,
	adaptiveCropAtom,
	adaptiveStraightenAtom,
	adaptivePerspectiveAtom,
	adaptiveMaskingAtom,
	adaptiveHealingAtom,
	applyAIAdjustmentsAtom,
	editorBackgroundAtom,
	BackgroundColor,
	deltaSceneThreshAtom,
	deltaTimeThreshAtom,
	faceDetectorConfidenceThresholdAtom,
	faceParserConfidenceThresholdAtom,
	rfdetrPersonConfidenceThresholdAtom,
	showInternalStylesAtom,
	themeModeAtom,
	isI18nLanguage,
	languageAtom,
	usePreferredLocaleAtom,
} from "@/store/settings-state"
import type { I18nLanguage, ThemeMode } from "@/store/settings-state"
import {
	getEditorBackgroundForTheme,
	getSystemThemeMediaQuery,
	resolveThemeMode,
} from "@/utils/theme-mode"
import {
	getPixiesetAuthInUrl,
	isColorMatchDebugEnabled,
	isAIDebugEnabled,
	isHealingDebugDownloadsEnabled,
	isPixiesetAuthEnabled,
	setColorMatchDebugEnabled,
	setAIDebugEnabled,
	setHealingDebugDownloadsEnabled,
	setPixiesetAuthEnabled,
	isPerformanceDebugEnabled,
	isPeopleMaskingEnabled,
	setPerformanceDebugEnabled,
	setPeopleMaskingEnabled,
	isRetouchingEnabled,
	setRetouchingEnabled,
	isRetouchingMasksDebugEnabled,
	setRetouchingMasksDebugEnabled,
	isViewReferencesDebugEnabled,
	setViewReferencesDebugEnabled,
	isGenerativeFillEnabled,
	setGenerativeFillEnabled,
} from "@/utils/measurement/feature-flag"
import { useMemo, useState } from "react"
import { aiDebugBorderAtom } from "@/store/photo/photos-state"
import { store } from "@/store/store"
import { syncImageInfosToAtom } from "@/store/processing/image-info"
import { useUserQuery } from "@/api/users-api"
import { queryClient } from "@/api/constants"
import { isStaffPlusRole } from "@/utils/user-role"
import { changeLanguageAtom } from "@/store/localization-state"
import { useTranslation } from "react-i18next"
import {
	CREATE_NEW_PROJECT_CARD_EXPERIMENT,
	EXPERIMENTS,
	getExperimentVariantOverride,
	setExperimentVariantOverride,
} from "@/utils/measurement/experiment"
import type { ExperimentDefinition } from "@/utils/measurement/experiment"
import { getEnabledLocales, isLocalizationEnabled } from "@/utils/localization-access"
import { useAppConfigQuery } from "@/api/app-config-api"

type SettingsLayout = "tabs" | "page"

interface SettingsContentProps {
	layout: SettingsLayout
}

const reloadAfterFlagChange = () => {
	// Trigger React Query refetch to update all components
	void queryClient.refetchQueries()
}

export function SettingsContent({ layout }: SettingsContentProps) {
	const [pixiesetAuth, setPixiesetAuth] = useState(isPixiesetAuthEnabled())
	const { data: user } = useUserQuery()
	const appConfigQuery = useAppConfigQuery()
	const enabledLocales = useMemo(
		() => getEnabledLocales(appConfigQuery.data),
		[appConfigQuery.data],
	)
	const { t } = useTranslation("components", { keyPrefix: "settingsCommon" })

	const enableFeatureFlags = isStaffPlusRole(user?.role)
	const localizationEnabled = isLocalizationEnabled(appConfigQuery.data)

	if (layout === "tabs") {
		return (
			<Tabs.Root
				defaultValue="auto-adjustments"
				className="flex h-[300px] w-[680px] flex-row"
			>
				<Tabs.List className="m-[-58px] mr-6 flex w-60 shrink-0 flex-col space-y-2 bg-base-900 p-[58px] pr-6">
					<Tab value="auto-adjustments">{t("autoAdjustments")}</Tab>
					<Tab value="adaptive-editing">{t("adaptiveEditing")}</Tab>
					<Tab value="ai-processing">{t("aiProcessing")}</Tab>
					<Tab value="app-theme">{t("appTheme")}</Tab>
					<Tab value="editor-preferences">{t("editorPreferences")}</Tab>
					{enableFeatureFlags && <Tab value="debug">{t("debug")}</Tab>}
					{enableFeatureFlags && <Tab value="experiments">{t("experiments")}</Tab>}
				</Tabs.List>{" "}
				<Tabs.Content value="auto-adjustments" className="w-full space-y-4">
					<AutoAdjustmentsSection />
				</Tabs.Content>
				<Tabs.Content value="adaptive-editing" className="w-full space-y-4">
					<AdaptiveEditingSection />
				</Tabs.Content>
				<Tabs.Content value="ai-processing" className="w-full space-y-4">
					<AIProcessingSection />
				</Tabs.Content>
				<Tabs.Content value="app-theme" className="w-full space-y-4">
					<AppThemeSection />
				</Tabs.Content>
				<Tabs.Content value="editor-preferences" className="w-full space-y-4">
					<EditorPreferencesSection />
				</Tabs.Content>
				{enableFeatureFlags && (
					<>
						<Tabs.Content
							value="debug"
							className="scrollarea -mr-4 w-full space-y-4 overflow-y-auto pr-4"
						>
							<DebugSection
								localizationEnabled={localizationEnabled}
								enabledLocales={enabledLocales}
							/>
							<ScrollBar visible="hover" />
						</Tabs.Content>
						<Tabs.Content
							value="experiments"
							className="scrollarea -mr-4 w-full space-y-4 overflow-y-auto pr-4"
						>
							<ExperimentsSection />
							<ScrollBar visible="hover" />
						</Tabs.Content>
					</>
				)}
			</Tabs.Root>
		)
	}

	// layout = "page"

	return (
		<div className="flex flex-col space-y-8">
			<SectionWrapper title={t("autoAdjustments")}>
				<AutoAdjustmentsSection layout="page" />
			</SectionWrapper>
			<SectionWrapper title={t("adaptiveEditing")}>
				<AdaptiveEditingSection layout="page" />
			</SectionWrapper>
			<SectionWrapper title={t("aiProcessing")}>
				<AIProcessingSection layout="page" />
			</SectionWrapper>
			<SectionWrapper title={t("appTheme")}>
				<AppThemeSection layout="page" />
			</SectionWrapper>
			<SectionWrapper title={t("editorPreferences")}>
				<EditorPreferencesSection layout="page" />
			</SectionWrapper>
			{enableFeatureFlags && (
				<>
					<SectionWrapper title={t("debug")}>
						<DebugSection
							layout="page"
							localizationEnabled={localizationEnabled}
							enabledLocales={enabledLocales}
						/>
					</SectionWrapper>

					<SectionWrapper title={t("experiments")}>
						<ExperimentsSection />
					</SectionWrapper>

					<SectionWrapper title={t("featureFlags")}>
						<SettingToggle
							trackingLabel="enable_pixieset_auth"
							aria-label={t("pixiesetEarlyAccessAria")}
							title={t("pixiesetEarlyAccess")}
							description={t("pixiesetEarlyAccessDescription")}
							checked={pixiesetAuth}
							disabled={getPixiesetAuthInUrl(new URL(location.href)) !== null}
							onCheckedChange={(checked) => {
								setPixiesetAuthEnabled(checked)
								setPixiesetAuth(checked)
								reloadAfterFlagChange()
							}}
						/>
					</SectionWrapper>
				</>
			)}
		</div>
	)
}

const Tab = (props: React.ComponentProps<typeof Tabs.Trigger>) => (
	<Tabs.Trigger
		className={cn(
			"whitespace-nowrap rounded-sm px-2.5 py-1.5 text-left text-sm leading-tight hover:bg-base-800 data-[state=active]:bg-base-700 data-[state=active]:text-base-100",
		)}
		{...props}
	/>
)

export const SectionWrapper = ({
	title,
	children,
}: {
	title: string
	children: React.ReactNode
}) => (
	<div className="flex flex-col space-y-4">
		<h2 className="text-md font-semibold text-base-400">{title}</h2>
		<div className="flex flex-col space-y-4 rounded-lg bg-base-700/50 px-6 py-4 dark:bg-base-700">
			{children}
		</div>
	</div>
)

export const SettingToggle = (props: {
	trackingLabel: string
	title: string
	description?: string
	checked: boolean
	disabled?: boolean
	onCheckedChange: (checked: boolean) => void
}) => {
	return (
		<div
			data-testid={`setting_${props.trackingLabel}`}
			className="flex flex-col space-y-2 text-sm"
		>
			<div className="flex flex-row items-center justify-between">
				<div>{props.title}</div>
				<Switch
					trackingLabel={props.trackingLabel}
					onCheckedChange={props.onCheckedChange}
					checked={props.checked}
					disabled={props.disabled}
				/>
			</div>
			{!!props.description && (
				<div className="text-xs text-base-400">{props.description}</div>
			)}
		</div>
	)
}

const SettingNumber = (props: {
	trackingLabel: string
	title: string
	description?: string
	value: number
	onChange: (value: number) => void
	min: number
	max: number
	step: number
}) => {
	return (
		<div className="flex flex-col space-y-4 text-sm">
			<div className="flex flex-row items-center justify-between">
				<div>{props.title}</div>
				<input
					type="number"
					className="w-24 rounded-sm bg-base-700 px-2 py-1 text-right text-sm"
					value={props.value}
					onChange={(e) => props.onChange(parseFloat(e.target.value))}
					min={props.min}
					max={props.max}
					step={props.step}
				/>
			</div>
			{!!props.description && (
				<div className="text-xs text-base-400">{props.description}</div>
			)}
		</div>
	)
}

const SettingSwatch = ({
	color,
	isSelected,
	onSelect,
}: {
	color: BackgroundColor
	isSelected: boolean
	onSelect: (color: BackgroundColor) => void
}) => (
	<div
		className={clsx(isSelected && "ring-2 ring-accent-500", "size-5 cursor-pointer rounded-sm")}
		style={{ background: color }}
		onClick={() => onSelect(color)}
	/>
)

const AutoAdjustmentsSection = ({ layout }: { layout?: SettingsLayout }) => {
	const [autoStraighten, setAutoStraighten] = useAtom(autoStraightenAtom)
	const [autoPerspective, setAutoPerspective] = useAtom(autoPerspectiveAtom)
	const [applyAIAdjustments, setApplyAIAdjustments] = useAtom(applyAIAdjustmentsAtom)
	const { t } = useTranslation("components", { keyPrefix: "settingsCommon" })

	return (
		<>
			<SettingToggle
				trackingLabel="auto_straighten"
				title={t("autoStraighten")}
				description={t("autoStraightenDescription")}
				checked={autoStraighten}
				onCheckedChange={() => setAutoStraighten(!autoStraighten)}
			/>
			{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}
			<SettingToggle
				trackingLabel="auto_perspective"
				aria-label={t("autoPerspectiveAria")}
				title={t("autoPerspectiveCorrection")}
				description={t("autoPerspectiveDescription")}
				checked={autoPerspective}
				onCheckedChange={() => setAutoPerspective(!autoPerspective)}
			/>
			{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}
			<SettingToggle
				trackingLabel="auto_adjustments"
				aria-label={t("autoAdjustmentsAria")}
				title={t("autoLightingWhiteBalance")}
				description={t("autoLightingDescription")}
				checked={applyAIAdjustments}
				onCheckedChange={() => setApplyAIAdjustments(!applyAIAdjustments)}
			/>
		</>
	)
}

const AdaptiveEditingSection = ({ layout }: { layout?: SettingsLayout }) => {
	const [adaptiveCrop, setAdaptiveCrop] = useAtom(adaptiveCropAtom)
	const [adaptiveStraighten, setAdaptiveStraighten] = useAtom(adaptiveStraightenAtom)
	const [adaptivePerspective, setAdaptivePerspective] = useAtom(adaptivePerspectiveAtom)
	const [adaptiveMasking, setAdaptiveMasking] = useAtom(adaptiveMaskingAtom)
	const [adaptiveHealing, setAdaptiveHealing] = useAtom(adaptiveHealingAtom)
	const { t } = useTranslation("components", { keyPrefix: "settingsCommon" })

	return (
		<>
			<SettingToggle
				trackingLabel="adaptive_crop"
				aria-label={t("adaptiveCropAria")}
				title={t("adaptiveCropping")}
				description={t("adaptiveCroppingDescription")}
				checked={adaptiveCrop}
				onCheckedChange={() => setAdaptiveCrop(!adaptiveCrop)}
			/>
			{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}
			<SettingToggle
				trackingLabel="adaptive_straighten"
				aria-label={t("adaptiveStraightenAria")}
				title={t("adaptiveStraightening")}
				description={t("adaptiveStraighteningDescription")}
				checked={adaptiveStraighten}
				onCheckedChange={() => setAdaptiveStraighten(!adaptiveStraighten)}
			/>
			{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}
			<SettingToggle
				trackingLabel="adaptive_perspective"
				aria-label={t("adaptivePerspectiveAria")}
				title={t("adaptivePerspectiveCorrection")}
				description={t("adaptivePerspectiveDescription")}
				checked={adaptivePerspective}
				onCheckedChange={() => setAdaptivePerspective(!adaptivePerspective)}
			/>
			{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}
			<SettingToggle
				trackingLabel="adaptive_masking"
				aria-label={t("adaptiveMaskingAria")}
				title={t("adaptiveMasking")}
				description={t("adaptiveMaskingDescription")}
				checked={adaptiveMasking}
				onCheckedChange={() => setAdaptiveMasking(!adaptiveMasking)}
			/>
			{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}
			<SettingToggle
				trackingLabel="adaptive_healing"
				aria-label={t("adaptiveHealingAria")}
				title={t("adaptiveHealing")}
				description={t("adaptiveHealingDescription")}
				checked={adaptiveHealing}
				onCheckedChange={() => setAdaptiveHealing(!adaptiveHealing)}
			/>
		</>
	)
}

const AIProcessingSection = (_props: { layout?: SettingsLayout }) => {
	const [forwardProcessing, setForwardProcessing] = useAtom(forwardProcessingAtom)
	const { t } = useTranslation("components", { keyPrefix: "settingsCommon" })

	return (
		<>
			<SettingToggle
				trackingLabel="forward_processing"
				aria-label={t("forwardProcessingAria")}
				title={t("forwardOnlyReprocessing")}
				description={t("forwardProcessingDescription")}
				checked={forwardProcessing}
				onCheckedChange={() => setForwardProcessing(!forwardProcessing)}
			/>
		</>
	)
}

const themeModes: ThemeMode[] = ["system", "light", "dark"]

const AppThemeSection = (_props: { layout?: SettingsLayout }) => {
	const [themeMode, setThemeMode] = useAtom(themeModeAtom)
	const [, setEditorBackground] = useAtom(editorBackgroundAtom)
	const { t } = useTranslation("components", { keyPrefix: "settingsCommon" })
	const themeLabelMap: Record<ThemeMode, string> = {
		system: t("system"),
		light: t("light"),
		dark: t("dark"),
	}

	return (
		<>
			<div className="flex flex-col space-y-2 text-sm">
				<div className="flex flex-row items-center justify-between">
					<div>{t("appTheme")}</div>
					<ToggleGroup.Root
						aria-label={t("appThemeAria")}
						type="single"
						value={themeMode}
						onValueChange={(value) => {
							if (value === "system" || value === "light" || value === "dark") {
								const resolvedTheme = resolveThemeMode(
									value,
									getSystemThemeMediaQuery()?.matches,
								)
								setThemeMode(value)
								setEditorBackground(getEditorBackgroundForTheme(resolvedTheme))
							}
						}}
						className="flex overflow-hidden rounded-sm border border-base-600 bg-base-700"
					>
						{themeModes.map((mode) => (
							<ToggleGroup.Item
								key={mode}
								value={mode}
								data-testid={`theme_${mode}`}
								className="min-w-[64px] border-l border-base-600 px-2 py-1 text-xs first:border-l-0 data-[state=on]:bg-base-500 data-[state=on]:text-base-100"
							>
								{themeLabelMap[mode]}
							</ToggleGroup.Item>
						))}
					</ToggleGroup.Root>
				</div>
				<div className="text-xs text-base-400">{t("appThemeDescription")}</div>
			</div>
		</>
	)
}

const EditorPreferencesSection = (_props: { layout?: SettingsLayout }) => {
	const [editorBackground, setEditorBackground] = useAtom(editorBackgroundAtom)
	const { t } = useTranslation("components", { keyPrefix: "settingsCommon" })

	return (
		<>
			<div className="flex flex-col space-y-2 text-sm">
				<div className="flex flex-row items-center justify-between">
					<div>{t("editorBackground")}</div>
					<div className="flex h-5 flex-row space-x-2">
						{Object.values(BackgroundColor).map((color) => (
							<SettingSwatch
								key={color}
								color={color}
								isSelected={editorBackground === color}
								onSelect={(c) => setEditorBackground(c)}
							/>
						))}
					</div>
				</div>
				<div className="text-xs text-base-400">{t("editorBackgroundDescription")}</div>
			</div>
		</>
	)
}

const LocalizationSection = ({ enabledLocales }: { enabledLocales: I18nLanguage[] }) => {
	const currentLanguage = useAtomValue(languageAtom)
	const changeLanguage = useSetAtom(changeLanguageAtom)
	const [usePreferredLocale, setUsePreferredLocale] = useAtom(usePreferredLocaleAtom)
	const { data: user } = useUserQuery()
	const { t } = useTranslation("components", { keyPrefix: "settingsCommon" })
	const selectedLocale = usePreferredLocale
		? "account"
		: enabledLocales.includes(currentLanguage)
			? currentLanguage
			: "en"
	const preferredLocale =
		user && isI18nLanguage(user.preferredLocale) ? user.preferredLocale : undefined

	return (
		<div className="flex flex-col space-y-4 text-sm">
			<div className="flex flex-row items-center justify-between">
				<div>{t("localization")}</div>
				<Select
					trackingLabel="locale"
					aria-label={t("localization")}
					value={selectedLocale}
					onValueChange={(value) => {
						if (value === "account") {
							setUsePreferredLocale(true)
							return
						}

						if (isI18nLanguage(value)) {
							setUsePreferredLocale(false)
							if (value !== currentLanguage) {
								void changeLanguage(value)
							}
						}
					}}
				>
					<SelectTrigger
						data-testid="locale_selector"
						className="ml-auto !w-auto"
						buttonClassName="h-7 min-w-[192px] justify-between rounded-sm border border-base-600 bg-base-700 px-2 text-xs"
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent align="end" side="bottom">
						<SelectGroup>
							<SelectItem value="account">{t("preferredLocale")}</SelectItem>
							{enabledLocales.map((language) => (
								<SelectItem
									key={language}
									value={language}
									className={clsx(
										usePreferredLocale &&
											preferredLocale === language &&
											"font-semibold",
									)}
								>
									{language}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
			</div>
			<div className="text-xs text-base-400">{t("localizationDescription")}</div>
		</div>
	)
}

const DebugSection = ({
	layout,
	localizationEnabled,
	enabledLocales,
}: {
	layout?: SettingsLayout
	localizationEnabled: boolean
	enabledLocales: I18nLanguage[]
}) => {
	const [deltaSceneThresh, setDeltaSceneThresh] = useAtom(deltaSceneThreshAtom)
	const [deltaTimeThresh, setDeltaTimeThresh] = useAtom(deltaTimeThreshAtom)
	const [faceDetectorConfidenceThreshold, setFaceDetectorConfidenceThreshold] = useAtom(
		faceDetectorConfidenceThresholdAtom,
	)
	const [faceParserConfidenceThreshold, setFaceParserConfidenceThreshold] = useAtom(
		faceParserConfidenceThresholdAtom,
	)
	const [rfdetrPersonConfidenceThreshold, setRfDetrPersonConfidenceThreshold] = useAtom(
		rfdetrPersonConfidenceThresholdAtom,
	)
	const [aiDebug, setAIDebug] = useState(isAIDebugEnabled())
	const [healingDebugDownloads, setHealingDebugDownloads] = useState(
		isHealingDebugDownloadsEnabled(),
	)
	const [performanceDebug, setPerformanceDebug] = useState(isPerformanceDebugEnabled())
	const [colorMatchDebug, setColorMatchDebug] = useState(isColorMatchDebugEnabled())
	const [viewReferencesDebug, setViewReferencesDebug] = useState(isViewReferencesDebugEnabled())
	const [generativeFillEnabled, setGenerativeFill] = useState(isGenerativeFillEnabled())
	const [peopleMaskingEnabled, setPeopleMasking] = useState(isPeopleMaskingEnabled())
	const [retouchingEnabled, setRetouching] = useState(isRetouchingEnabled())
	const [retouchingMasksDebug, setRetouchingMasksDebug] = useState(
		isRetouchingMasksDebugEnabled(),
	)
	const [showInternalStyles, setShowInternalStyles] = useAtom(showInternalStylesAtom)
	const { t } = useTranslation("components", { keyPrefix: "settingsCommon" })

	return (
		<>
			<SettingToggle
				trackingLabel="enable_performance_debug"
				aria-label={t("performanceDebugAria")}
				title={t("performanceMetrics")}
				description={t("performanceMetricsDescription")}
				checked={performanceDebug}
				onCheckedChange={(checked) => {
					setPerformanceDebugEnabled(checked)
					setPerformanceDebug(checked)
					reloadAfterFlagChange()
				}}
			/>

			<div className="h-[0.5px] w-full bg-base-600" />

			<SettingToggle
				trackingLabel="enable_generative_fill"
				aria-label={t("generativeFillAria")}
				title={t("aiGenerateErase")}
				description={t("aiGenerateEraseDescription")}
				checked={generativeFillEnabled}
				onCheckedChange={(checked) => {
					setGenerativeFillEnabled(checked)
					setGenerativeFill(checked)
					reloadAfterFlagChange()
				}}
			/>

			<div className="h-[0.5px] w-full bg-base-600" />

			<SettingToggle
				trackingLabel="enable_ai_debug_mode"
				aria-label={t("aiDebugAria")}
				title={t("aiDebugMode")}
				description={t("aiDebugDescription")}
				checked={aiDebug}
				onCheckedChange={(checked) => {
					setAIDebugEnabled(checked)
					setAIDebug(checked)
					if (checked) {
						// Sync existing imageInfo records to atom when enabling debug mode to update thumbnail borders
						syncImageInfosToAtom()
					} else {
						// Clear all thumbnail borders when disabling debug mode
						store.set(aiDebugBorderAtom, {})
					}
					reloadAfterFlagChange()
				}}
			/>

			<div className="h-[0.5px] w-full bg-base-600" />

			{aiDebug && (
				<>
					<SettingToggle
						trackingLabel="enable_healing_debug_downloads"
						aria-label={t("healingDebugDownloadsAria")}
						title={t("healingDebugDownloads")}
						description={t("healingDebugDownloadsDescription")}
						checked={healingDebugDownloads}
						onCheckedChange={(checked) => {
							setHealingDebugDownloadsEnabled(checked)
							setHealingDebugDownloads(checked)
							reloadAfterFlagChange()
						}}
					/>

					<div className="h-[0.5px] w-full bg-base-600" />
				</>
			)}

			<SettingToggle
				trackingLabel="enable_color_match_debug"
				aria-label={t("colorMatchDebugAria")}
				title={t("colorMatch")}
				description={t("colorMatchDescription")}
				checked={colorMatchDebug}
				onCheckedChange={(checked) => {
					setColorMatchDebugEnabled(checked)
					setColorMatchDebug(checked)
					reloadAfterFlagChange()
				}}
			/>
			<div className="h-[0.5px] w-full bg-base-600" />

			<SettingToggle
				trackingLabel="enable_view_references_debug"
				aria-label={t("viewReferencesDebugAria")}
				title={t("viewReferences")}
				description={t("viewReferencesDescription")}
				checked={viewReferencesDebug}
				onCheckedChange={(checked) => {
					setViewReferencesDebugEnabled(checked)
					setViewReferencesDebug(checked)
					reloadAfterFlagChange()
				}}
			/>

			<div className="h-[0.5px] w-full bg-base-600" />

			<SettingToggle
				trackingLabel="enable_retouching"
				aria-label={t("toggleRetouching")}
				title={t("retouching")}
				description={t("enableTheExperimentalPeopleRetouching")}
				checked={retouchingEnabled}
				onCheckedChange={(checked) => {
					setRetouchingEnabled(checked)
					setRetouching(checked)
					location.reload()
				}}
			/>

			<div className="h-[0.5px] w-full bg-base-600" />

			{retouchingEnabled && (
				<>
					<SettingToggle
						trackingLabel="show_retouching_masks_debug"
						aria-label={t("toggleRetouchingMasksDebug")}
						title={t("showRetouchingMasks")}
						description={t("showMasksCreatedInternallyBy")}
						checked={retouchingMasksDebug}
						onCheckedChange={(checked) => {
							setRetouchingMasksDebugEnabled(checked)
							setRetouchingMasksDebug(checked)
							location.reload()
						}}
					/>

					<div className="h-[0.5px] w-full bg-base-600" />
				</>
			)}

			<SettingToggle
				trackingLabel="enable_people_masking"
				aria-label={t("togglePeopleMasking")}
				title={t("peopleMasking")}
				description={t("enableTheExperimentalPeopleAnd")}
				checked={peopleMaskingEnabled}
				onCheckedChange={(checked) => {
					setPeopleMaskingEnabled(checked)
					setPeopleMasking(checked)
					location.reload()
				}}
			/>

			<div className="h-[0.5px] w-full bg-base-600" />

			<SettingToggle
				trackingLabel="show_internal_styles"
				aria-label={t("internalStylesAria")}
				title={t("showInternalStyles")}
				description={t("showInternalStylesDescription")}
				checked={showInternalStyles}
				onCheckedChange={(checked) => {
					setShowInternalStyles(checked)
					reloadAfterFlagChange()
				}}
			/>

			<div className="h-[0.5px] w-full bg-base-600" />

			<SettingNumber
				trackingLabel="delta_scene_thresh"
				aria-label={t("sceneThresholdAria")}
				title={t("sceneDistanceThreshold")}
				description={t("sceneThresholdDescription")}
				value={deltaSceneThresh}
				onChange={setDeltaSceneThresh}
				min={-0.1}
				max={0.2}
				step={0.01}
			/>

			{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}

			<SettingNumber
				trackingLabel="delta_time_thresh"
				aria-label={t("timeThresholdAria")}
				title={t("timeWindowDelta")}
				description={t("timeThresholdDescription")}
				value={deltaTimeThresh}
				onChange={setDeltaTimeThresh}
				min={-60}
				max={600}
				step={60}
			/>

			{localizationEnabled && (
				<>
					<div className="h-[0.5px] w-full bg-base-600" />
					<LocalizationSection enabledLocales={enabledLocales} />
				</>
			)}

			{retouchingEnabled && peopleMaskingEnabled && (
				<>
					{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}

					<SettingNumber
						trackingLabel="face_detector_confidence_threshold"
						aria-label={t("adjustFaceDetectorConfidenceThreshold")}
						title={t("faceDetectorConfidenceThreshold")}
						description={t("minimumRetinafaceConfidenceForFace")}
						value={faceDetectorConfidenceThreshold}
						onChange={setFaceDetectorConfidenceThreshold}
						min={0}
						max={1}
						step={0.01}
					/>

					{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}

					<SettingNumber
						trackingLabel="face_parser_confidence_threshold"
						aria-label={t("adjustFaceParserConfidenceThreshold")}
						title={t("faceParserPixelConfidenceThreshold")}
						description={t("minimumSoftmaxConfidenceForKeeping")}
						value={faceParserConfidenceThreshold}
						onChange={setFaceParserConfidenceThreshold}
						min={0}
						max={1}
						step={0.01}
					/>

					{layout === "page" && <div className="h-[0.5px] w-full bg-base-600" />}

					<SettingNumber
						trackingLabel="rfdetr_person_confidence_threshold"
						aria-label={t("adjustRfdetrPersonConfidenceThreshold")}
						title={t("rfdetrPersonConfidenceThreshold")}
						description={t("minimumSigmoidPersonclassConfidenceFor")}
						value={rfdetrPersonConfidenceThreshold}
						onChange={setRfDetrPersonConfidenceThreshold}
						min={0}
						max={1}
						step={0.01}
					/>
				</>
			)}
		</>
	)
}

const ExperimentsSection = () => {
	return (
		<>
			{EXPERIMENTS.map((experiment) => (
				<ExperimentSetting key={experiment.key} experiment={experiment} />
			))}
			<ExperimentSetting experiment={CREATE_NEW_PROJECT_CARD_EXPERIMENT} provider="backend" />
		</>
	)
}

const ExperimentSetting = ({
	experiment,
	provider = "amplitude",
}: {
	experiment: ExperimentDefinition
	provider?: "amplitude" | "backend"
}) => {
	const { t } = useTranslation("components")
	const [variant, setVariant] = useState(
		() => getExperimentVariantOverride(experiment) ?? provider,
	)

	return (
		<div className="flex flex-col space-y-2 text-sm">
			<div className="flex flex-row items-center justify-between">
				<div>{experiment.title}</div>
				<ToggleGroup.Root
					aria-label={t("settingsCommon.experiment", { title: experiment.title })}
					type="single"
					value={variant}
					onValueChange={(value) => {
						if (value !== provider && !experiment.variants.includes(value)) {
							return
						}

						setExperimentVariantOverride(experiment, value === provider ? null : value)
						setVariant(value)
					}}
					className="flex overflow-hidden rounded-sm border border-base-600 bg-base-700"
				>
					{[provider, ...experiment.variants].map((value) => (
						<ToggleGroup.Item
							key={value}
							value={value}
							className="border-l border-base-600 px-2 py-1 text-xs capitalize first:border-l-0 data-[state=on]:bg-base-500 data-[state=on]:text-base-100"
						>
							{value}
						</ToggleGroup.Item>
					))}
				</ToggleGroup.Root>
			</div>
		</div>
	)
}
